import React, { useMemo } from 'react';
import { Box, BoxProps } from '@mui/material';
import { OneOfPropertyElements } from '@shared/models/project.model';
import { IElementProperty } from '@shared/models/element-property.model';
import {
  isElementPropertySourceCategory,
  isFullyEditableProperty,
} from '@shared/helpers/element-property.helpers';
import { IElementQuantityProperty } from '@shared/models/element-quantities.model';
import { useIsReadonlyElement } from '@client/hooks/use-user.hooks';
import Droppable from '@ui/drag-and-drop/Droppable';
import Draggable from '@ui/drag-and-drop/Draggable';
import { useMoveProperty } from '@client/hooks/use-move-elements.hooks';
import ElementProperty from './ElementProperty';
import { EmptyStateMessage } from '../../../../EmptyStateMessage';

type Layout = 'one-column' | 'two-column';

type ElementPropertiesProps = {
  element: OneOfPropertyElements;
  emptyStateCopy?: string;
  /** Center-align the empty-state message (e.g. building version accordion). */
  emptyStateCentered?: boolean;
  layout?: Layout;
  /**
   * Properties to list.
   * Note that not every property from element might be listed.
   */
  properties: (IElementProperty | IElementQuantityProperty)[];
};

const layoutProperties: Record<
  Layout,
  { boxProps: BoxProps; showIcons: boolean }
> = {
  'one-column': {
    boxProps: {
      sx: {
        display: 'flex',
        flexDirection: 'column',
      },
    },
    showIcons: false,
  },
  'two-column': {
    boxProps: {
      sx: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 1,
      },
    },
    showIcons: true,
  },
};

export const ElementPropertiesForm: React.FC<ElementPropertiesProps> = ({
  element,
  properties,
  layout = 'one-column',
  emptyStateCopy,
  emptyStateCentered = false,
}) => {
  const moveProperty = useMoveProperty(element);
  const readonly = useIsReadonlyElement(element);

  const { showIcons, boxProps } = layoutProperties[layout];

  const visibleProperties = useMemo(
    () => properties.filter((p) => !p.hidden),
    [properties],
  );

  /** If all properties are not editable, then we don't need to show the kebab menu.
   * We do it here to keep aligment for the proprety inputs */
  const hideAllKebabMenus =
    readonly || properties.every((p) => !isFullyEditableProperty(p));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box {...boxProps}>
        {visibleProperties.map((property) => {
          // Same element + name survives enrichment; property.id does not. Include
          // element.id so switching selection remounts instead of reusing the row.
          const rowKey = `${element.id}-${property.name}`;
          // Quantity and category properties are not draggable
          if (isElementPropertySourceCategory(property)) {
            return (
              <ElementProperty
                key={rowKey}
                property={property}
                element={element}
                hideKebabMenu={hideAllKebabMenus}
                showIcon={showIcons}
              />
            );
          }
          return (
            <Droppable
              key={rowKey}
              allowDropAbove
              allowDropBelow
              allowDropInside={false}
              dropId={property.id}
              onDrop={moveProperty}
            >
              <Draggable dragId={property.id}>
                <ElementProperty
                  property={property}
                  element={element}
                  hideKebabMenu={hideAllKebabMenus}
                  showIcon={showIcons}
                />
              </Draggable>
            </Droppable>
          );
        })}
      </Box>
      {visibleProperties.length === 0 && emptyStateCopy && (
        <EmptyStateMessage
          message={emptyStateCopy}
          centered={emptyStateCentered}
        />
      )}
    </Box>
  );
};
